import { Box, Callout, Flex, RadioGroup, Select, Text, TextArea, TextField } from '@radix-ui/themes'
import { AlertCircle } from 'lucide-react'
import type {
  BaseInputTemplateProps,
  ErrorListProps,
  FieldTemplateProps,
  ObjectFieldTemplateProps,
  RegistryWidgetsType,
  WidgetProps,
} from '@rjsf/utils'

/**
 * Radix widgets for react-jsonschema-form, so a track's credential form looks
 * like every other form in the app instead of like RJSF's unstyled base theme.
 *
 * RJSF only supplies the schema-driven plumbing here — every element the
 * applicant actually sees is ours. Sizes match `media_group_participants/_form`:
 * size-5 labels over size-3 controls.
 */

/** Options come back as `{ value, label }`, honouring `ui:enumNames`. */
type EnumOption = { value: unknown; label: string }

function enumOptionsOf(props: WidgetProps): EnumOption[] {
  return (props.options?.enumOptions ?? []) as EnumOption[]
}

// Radio and Select speak strings; the schema may not (e.g. an integer enum), so
// round-trip through the option list rather than handing back the string.
function valueFor(options: EnumOption[], selected: string): unknown {
  return options.find((option) => String(option.value) === selected)?.value
}

/** Text, email, and number inputs all land here. */
export function BaseInputTemplate(props: BaseInputTemplateProps) {
  const { id, value, required, disabled, readonly, autofocus, placeholder, onChange, onBlur, onFocus, options, schema } =
    props

  const isNumeric = schema.type === 'number' || schema.type === 'integer'
  const type = isNumeric ? 'number' : schema.format === 'email' ? 'email' : 'text'

  const handleChange = (raw: string) => {
    if (raw === '') return onChange(options.emptyValue ?? undefined)
    onChange(isNumeric ? Number(raw) : raw)
  }

  return (
    <TextField.Root
      id={id}
      size="3"
      type={type}
      value={value ?? ''}
      required={required}
      disabled={disabled || readonly}
      autoFocus={autofocus}
      placeholder={placeholder}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={(e) => onBlur(id, e.target.value)}
      onFocus={(e) => onFocus(id, e.target.value)}
    />
  )
}

export function TextareaWidget(props: WidgetProps) {
  const { id, value, required, disabled, readonly, autofocus, placeholder, onChange, options } = props

  return (
    <TextArea
      id={id}
      size="3"
      rows={(options.rows as number) ?? 4}
      value={value ?? ''}
      required={required}
      disabled={disabled || readonly}
      autoFocus={autofocus}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value === '' ? options.emptyValue ?? undefined : e.target.value)}
    />
  )
}

/**
 * The workhorse: nearly every question on these forms is a radio with a long,
 * sentence-length option label. Stacked, full width, wrapping — never squeezed.
 */
export function RadioWidget(props: WidgetProps) {
  const { id, value, disabled, readonly, onChange } = props
  const options = enumOptionsOf(props)

  return (
    <RadioGroup.Root
      id={id}
      size="2"
      value={value === undefined || value === null ? '' : String(value)}
      onValueChange={(selected) => onChange(valueFor(options, selected))}
    >
      <Flex direction="column" gap="2">
        {options.map((option) => (
          <Text as="label" size="3" key={String(option.value)}>
            <Flex align="start" gap="2">
              <RadioGroup.Item value={String(option.value)} disabled={disabled || readonly} />
              <Text>{option.label}</Text>
            </Flex>
          </Text>
        ))}
      </Flex>
    </RadioGroup.Root>
  )
}

export function SelectWidget(props: WidgetProps) {
  const { id, value, required, disabled, readonly, placeholder, onChange } = props
  const options = enumOptionsOf(props)

  return (
    <Select.Root
      size="3"
      required={required}
      disabled={disabled || readonly}
      value={value === undefined || value === null ? '' : String(value)}
      onValueChange={(selected) => onChange(valueFor(options, selected))}
    >
      <Select.Trigger id={id} placeholder={placeholder || 'Select...'} />
      <Select.Content>
        {options.map((option) => (
          <Select.Item key={String(option.value)} value={String(option.value)}>
            {option.label}
          </Select.Item>
        ))}
      </Select.Content>
    </Select.Root>
  )
}

/** Wraps every field: label above, control below, error underneath. */
export function FieldTemplate(props: FieldTemplateProps) {
  const { id, label, required, description, children, rawErrors, help, hidden, displayLabel } = props

  if (hidden) return <>{children}</>

  const errors = rawErrors ?? []

  return (
    <Box mb="6">
      <Flex direction="column" gap="2">
        {displayLabel && label && (
          <Text as="label" size="5" htmlFor={id}>
            {label}
            {required && ' *'}
          </Text>
        )}
        {displayLabel && description}
        {children}
        {errors.length > 0 && (
          <Text size="2" color="red">
            {errors.join('. ')}
          </Text>
        )}
        {help}
      </Flex>
    </Box>
  )
}

/** The form body. The page owns the heading, so we don't render the root title. */
export function ObjectFieldTemplate(props: ObjectFieldTemplateProps) {
  return (
    <Flex direction="column" my="5">
      {props.properties.map((property) => (
        <Box key={property.name}>{property.content}</Box>
      ))}
    </Flex>
  )
}

/** RJSF's summary of everything wrong, shown on a failed submit. */
export function ErrorListTemplate({ errors }: ErrorListProps) {
  return (
    <Callout.Root color="red" role="alert" mb="4">
      <Callout.Icon>
        <AlertCircle size={18} />
      </Callout.Icon>
      <Callout.Text>
        <Text weight="bold">Some answers still need attention:</Text>
        <Box mt="1">
          {errors.map((error, i) => (
            <Box key={i}>{error.stack}</Box>
          ))}
        </Box>
      </Callout.Text>
    </Callout.Root>
  )
}

export const radixWidgets: RegistryWidgetsType = {
  TextWidget: BaseInputTemplate,
  EmailWidget: BaseInputTemplate,
  TextareaWidget,
  RadioWidget,
  SelectWidget,
}

export const radixTemplates = {
  BaseInputTemplate,
  FieldTemplate,
  ObjectFieldTemplate,
  ErrorListTemplate,
}
